/**
 * Notification SSE Hook
 *
 * Connects to Central's notification SSE stream and invalidates local
 * React Query caches for near-instant notification updates.
 *
 * Uses fetch-based SSE (not EventSource) to support Authorization header.
 * Does NOT send Cache-Control header to avoid CORS preflight rejection
 * from Central (central-staging does not allow it).
 *
 * LIFECYCLE:
 * - Connects when boot is ready (workspaceId + token available)
 * - Disconnects on unmount or logout
 * - Reconnects automatically on token/workspace change
 *
 * POLLING FALLBACK:
 * Existing React Query polling (60s) is intentionally retained as a safety net.
 * SSE provides near-instant updates; polling catches anything missed.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import {
  NOTIFICATION_STREAM_PATH,
  parseSSEBuffer,
  type CentralNotification,
  type UnreadCountEvent,
} from '@levelup/central-notifications';
import { getAccessToken } from '../lib/centralAuth';
import type { RootState } from '../store';

const RAW_CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL;
const MAX_RETRIES = 6;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 30000;

function getCentralBase(): string | null {
  if (!RAW_CENTRAL_URL) return null;
  return RAW_CENTRAL_URL.replace(/\/+$/, '');
}

/**
 * Hook that manages the Central notification SSE lifecycle.
 *
 * Must be rendered inside BootGate (only mounts when boot status is 'ready').
 */
export function useNotificationSSE(): void {
  const queryClient = useQueryClient();

  const workspaceId = useSelector(
    (state: RootState) => state.boot.context?.currentWorkspace?.id ?? null,
  );
  const token = useSelector((state: RootState) => state.auth.token);

  const abortRef = useRef<AbortController | null>(null);
  const retryRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleNotification = useCallback(
    (_notification: CentralNotification) => {
      queryClient.invalidateQueries({ queryKey: ['workspaceNotifications'] });
      queryClient.invalidateQueries({ queryKey: ['workspaceUnreadCount'] });
    },
    [queryClient],
  );

  const handleUnreadCount = useCallback(
    (_event: UnreadCountEvent) => {
      queryClient.invalidateQueries({ queryKey: ['workspaceUnreadCount'] });
    },
    [queryClient],
  );

  useEffect(() => {
    const centralBase = getCentralBase();
    if (!centralBase || !workspaceId || !token) return;

    const accessToken = getAccessToken();
    if (!accessToken) return;

    let cancelled = false;

    async function connectSSE() {
      if (cancelled) return;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const url = `${centralBase}${NOTIFICATION_STREAM_PATH}?workspaceId=${encodeURIComponent(workspaceId!)}`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            console.warn('[notification-sse] auth error:', response.status);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        if (!response.body) throw new Error('No response body');

        console.log('[notification-sse] connection: up');
        retryRef.current = 0;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const { parsed, remaining } = parseSSEBuffer(buffer);
          buffer = remaining;

          for (const msg of parsed) {
            if (msg.type === 'notification' && msg.data) {
              handleNotification(msg.data as CentralNotification);
            } else if (msg.type === 'unread-count' && msg.data) {
              handleUnreadCount(msg.data as UnreadCountEvent);
            }
            // heartbeat — no action needed
          }
        }
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.warn('[notification-sse] stream error:', error);
      }

      // Reconnect with exponential backoff
      if (!cancelled && retryRef.current < MAX_RETRIES) {
        const delay = Math.min(
          BACKOFF_BASE_MS * Math.pow(2, retryRef.current),
          BACKOFF_MAX_MS,
        );
        retryRef.current++;
        console.log(`[notification-sse] reconnecting in ${delay}ms (attempt ${retryRef.current})`);
        retryTimeoutRef.current = setTimeout(connectSSE, delay);
      } else if (!cancelled) {
        console.log('[notification-sse] connection: down (max retries exceeded)');
      }
    }

    connectSSE();

    return () => {
      cancelled = true;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [workspaceId, token, handleNotification, handleUnreadCount]);
}
